import { useState, useEffect } from "react";

// Utility function for debouncing
export const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
};

// Shared mouse position state that components can subscribe to
let mousePosition = { x: 0.5, y: 0.5 }; // Normalized (0-1)
let listeners = [];

// Handle global mouse move once for the entire app
const handleGlobalMouseMove = (e) => {
    // Calculate normalized coordinates (0-1)
    mousePosition = {
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
    };

    // Notify all listeners with throttled updates
    window.requestAnimationFrame(() => {
        listeners.forEach((listener) => listener(mousePosition));
    });
};

// Initialize global mouse tracking
let isInitialized = false;
const initializeGlobalTracking = () => {
    if (!isInitialized && typeof window !== "undefined") {
        // Use passive event listener for better performance
        window.addEventListener("mousemove", handleGlobalMouseMove, {
            passive: true,
        });
        isInitialized = true;
    }
};

// Hook for using mouse parallax with performance optimization
export const useMouseParallax = (options = {}) => {
    const {
        strength = 30,
        ease = 0.1,
        elementRef = null,
        disabled = false,
    } = options;

    const [elements, setElements] = useState([]);

    useEffect(() => {
        // Only track if not disabled
        if (disabled) return;

        initializeGlobalTracking();

        // Function to update element positions
        const updateElementPositions = (mousePos) => {
            if (!elementRef?.current) return;

            const container = elementRef.current;
            const floatingElements =
                container.querySelectorAll(".floating-element");
            const rect = container.getBoundingClientRect();

            // Adjust mouse position relative to container
            const relativeX =
                (mousePos.x * window.innerWidth - rect.left) / rect.width;
            const relativeY =
                (mousePos.y * window.innerHeight - rect.top) / rect.height;

            // Only calculate for visible elements in container
            if (
                rect.top < window.innerHeight &&
                rect.bottom > 0 &&
                rect.left < window.innerWidth &&
                rect.right > 0
            ) {
                elements.forEach((el, index) => {
                    if (!el) return;

                    const factor = (index + 1) * 0.02;
                    const x = (relativeX - 0.5) * factor * strength;
                    const y = (relativeY - 0.5) * factor * strength;

                    // Use transform translate for better performance
                    el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
                });
            }
        };

        // Save reference to elements for better performance
        if (elementRef?.current) {
            const floatingElements =
                elementRef.current.querySelectorAll(".floating-element");
            setElements(Array.from(floatingElements));
        }

        // Add this component as a listener
        const listenerFn = updateElementPositions;
        listeners.push(listenerFn);

        return () => {
            // Clean up listener
            listeners = listeners.filter((listener) => listener !== listenerFn);
        };
    }, [elementRef, strength, disabled]);

    return { elementRef };
};

// Clean up when no longer needed
export const cleanupMouseTracking = () => {
    if (isInitialized && typeof window !== "undefined") {
        window.removeEventListener("mousemove", handleGlobalMouseMove);
        isInitialized = false;
        listeners = [];
    }
};
